import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CRUDPage } from "../../../components/ui/CRUDPage";
import { CRUDViewMode } from "../../../types";
import { AdminClient } from "../../../api/generated/APIClient";
import "./ContactPage.css";

// 一覧・詳細で使用するフィールド定義
// StructDocs が空スタブのため、メタデータの代わりにこの定義を CRUDPage へ渡す。
const CONTACT_FIELDS = [
  { protoName: "isRead", name: "状態", type: "bool", comment: "未読/既読" },
  { protoName: "createDate", name: "受信日時", type: "Date", comment: "" },
  { protoName: "name", name: "お名前", type: "string", comment: "" },
  { protoName: "email", name: "メールアドレス", type: "string", comment: "" },
  { protoName: "phone", name: "電話番号", type: "string", comment: "" },
  { protoName: "subject", name: "件名", type: "string", comment: "" },
  { protoName: "message", name: "お問い合わせ内容", type: "string", comment: "" },
  { protoName: "readAt", name: "既読日時", type: "Date", comment: "" },
  { protoName: "id", name: "ID", type: "string", comment: "" },
];

const DEFAULT_VISIBLE_COLUMNS = [
  "isRead",
  "createDate",
  "name",
  "email",
  "phone",
  "subject",
  "message",
];

// Timestamp / Date / 文字列を Date へ正規化する
const toDate = (value: any): Date | null => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "object" && value.seconds !== undefined) {
    const seconds = Number(value.seconds);
    const nanos = value.nanos ? Number(value.nanos) : 0;
    const date = new Date(seconds * 1000 + Math.floor(nanos / 1_000_000));
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const formatContactDateTime = (value: any): React.ReactNode => {
  const date = toDate(value);
  if (!date) return "-";

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const w = ["日", "月", "火", "水", "木", "金", "土"][date.getDay()];
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${y}/${m}/${d}(${w}) ${hh}:${mm}`;
};

export const ContactPage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<any[]>([]);
  const [currentContact, setCurrentContact] = useState<any | undefined>();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [pendingReadIds, setPendingReadIds] = useState<Set<string>>(new Set());

  // ルートからモードを判定（お問い合わせは作成不可）
  const getMode = (): CRUDViewMode => {
    const path = location.pathname;
    if (path.includes("/edit")) return "edit";
    // 作成モードは許可しない（一覧にリダイレクト）
    if (path.includes("/create")) {
      navigate("/admin");
      return "list";
    }
    if (id) return "detail";
    return "list";
  };

  // Connect-ES APIクライアント呼び出し関数（システムエンティティ用）
  const callSystemEntityAPI = async (operation: 'index' | 'create' | 'update' | 'delete', data?: any, ids?: string[]) => {
    try {
      // エンティティ名をPascalCaseに変換（Connect-ESメソッド名に合わせる）
      const entityPascalCase = 'Contact';
      
      // APIメソッド名を構築（Connect-ESの命名規則に従う）
      let methodName: string;
      switch (operation) {
        case 'index':
          methodName = `Index${entityPascalCase}`;
          break;
        case 'create':
          methodName = `Create${entityPascalCase}`;
          break;
        case 'update':
          methodName = `Update${entityPascalCase}`;
          break;
        case 'delete':
          methodName = `Delete${entityPascalCase}`;
          break;
        default:
          throw new Error(`未対応の操作: ${operation}`);
      }
      
      // AdminClientのメソッドを取得
      const apiMethod = (AdminClient as any)[methodName];
      if (!apiMethod || typeof apiMethod !== 'function') {
        throw new Error(`APIメソッド ${methodName} が見つかりません`);
      }
      
      // API引数を構築（システムエンティティ用）
      let apiArgs: any = {};
      if (operation === 'index') {
        apiArgs = {
          offset: 0,
          limit: 0,
        };
      } else if (operation === 'delete' && ids) {
        apiArgs = {
          ids: ids,
        };
      } else if (data) {
        // create/updateの場合は適切なフィールド名でデータを送信
        const entityArrayKey = `contacts`;
        apiArgs = {
          [entityArrayKey]: [data],
        };
      }
            
      // Connect-ES APIを呼び出し
      const result = await apiMethod(apiArgs);
      return result;
      
    } catch (error) {
      console.error(`❌ ${operation} request failed (System Entity):`, error);
      throw error;
    }
  };

  useEffect(() => {
    // Connect-ES APIからシステムエンティティデータを取得
    const fetchContacts = async () => {
      try {
        const response = await callSystemEntityAPI('index');        
        // Connect-ESレスポンスの構造を解析
        let items: any[] = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            items = response.data;
          } else if (typeof response.data === 'object') {
            const dataObj = response.data as any;
            // Connect-ESの一般的なレスポンス構造を確認
            if (dataObj.contacts && Array.isArray(dataObj.contacts)) {
              items = dataObj.contacts;
            } else if (dataObj.items && Array.isArray(dataObj.items)) {
              items = dataObj.items;
            } else if (dataObj.contacts === null || dataObj.items === null) {
              // nullの場合は空配列を設定
              items = [];
            } else if (Object.keys(dataObj).length === 0) {
              // 空のオブジェクトの場合は空配列を設定
              items = [];
            } else if (dataObj.id) {
              // 有効なIDを持つ単一オブジェクトの場合は配列に変換
              items = [dataObj];
            } else {
              // IDがない無効なオブジェクトの場合は空配列
              items = [];
            }
          }
        }
        setContacts(items);
      } catch (error) {
        console.error('❌ Contact取得エラー (System Entity):', error);
        // エラー時は空配列を設定
        setContacts([]);
      }
    };

    fetchContacts();
  }, []);

  useEffect(() => {
    // 現在のアイテムを設定
    if (id) {
      const item = contacts.find((item) => item.id === id);
      setCurrentContact(item);
    } else {
      setCurrentContact(undefined);
    }
  }, [id, contacts]);

  // 既読状態をサーバーへ反映し、ローカル状態も更新する
  // silent=true のときは失敗してもアラートを出さない（詳細を開いた際の自動既読用）
  const applyReadState = useCallback(
    async (contactId: string, isRead: boolean, silent = false) => {
      if (!contactId || pendingReadIds.has(contactId)) return;

      setPendingReadIds((prev) => new Set(prev).add(contactId));

      // 先にローカルを更新して即座に反映（失敗時は元に戻す）
      setContacts((prev) =>
        prev.map((item) =>
          item.id === contactId
            ? { ...item, isRead, readAt: isRead ? new Date() : undefined }
            : item
        )
      );

      try {
        const apiMethod = isRead
          ? AdminClient.MarkContactAsRead
          : AdminClient.MarkContactAsUnread;
        const result = await apiMethod({ contactId });
        if ((result as any)?.error) {
          throw new Error((result as any).error);
        }
      } catch (error) {
        console.error("❌ 既読状態の更新に失敗:", error);
        // 失敗したので元の状態へ戻す
        setContacts((prev) =>
          prev.map((item) =>
            item.id === contactId ? { ...item, isRead: !isRead } : item
          )
        );
        if (!silent) {
          alert(
            `既読状態の更新に失敗しました: ${
              error instanceof Error ? error.message : "不明なエラー"
            }`
          );
        }
      } finally {
        setPendingReadIds((prev) => {
          const next = new Set(prev);
          next.delete(contactId);
          return next;
        });
      }
    },
    [pendingReadIds]
  );

  // 詳細を開いたら自動的に既読にする
  // 失敗しても再試行しないよう、1件につき1回だけ実行する（無限リトライ防止）
  const autoReadAttemptedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!id || !currentContact) return;
    if (currentContact.isRead) return;
    if (autoReadAttemptedIds.current.has(id)) return;

    autoReadAttemptedIds.current.add(id);
    void applyReadState(id, true, true);
    // applyReadState は pendingReadIds に依存するため、対象IDと既読状態のみを監視する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentContact?.isRead]);

  const handleEdit = (itemId: string) => {
    navigate(`/admin/${itemId}/edit`);
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm("お問い合わせを削除しますか？")) {
      return;
    }

    try {      
      // Connect-ES API削除リクエストを送信
      await callSystemEntityAPI('delete', undefined, [itemId]);
      
      // ローカル状態を更新
      setContacts(contacts.filter((item) => item.id !== itemId));
      
      // 詳細ページから削除した場合は一覧ページに移動
      if (id === itemId) {
        navigate(`/admin`);
      }
      
    } catch (error) {
      console.error('❌ 削除エラー:', error);
      alert(`削除に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    }
  };

  const handleBack = () => {
    navigate(`/admin`);
  };

  // 一覧の「状態」カラム: 未読/既読バッジ兼トグルボタン
  const renderReadStateCell = useCallback(
    (value: any, item: any): React.ReactNode => {
      const isRead = Boolean(value);
      const isPending = pendingReadIds.has(item?.id);
      return (
        <button
          type="button"
          className={`contact-read-badge ${isRead ? "is-read" : "is-unread"}`}
          disabled={isPending}
          title={isRead ? "クリックで未読に戻す" : "クリックで既読にする"}
          onClick={(e) => {
            e.stopPropagation();
            void applyReadState(item?.id, !isRead);
          }}
        >
          {isRead ? "既読" : "● 未読"}
        </button>
      );
    },
    [applyReadState, pendingReadIds]
  );

  // 一覧の「お問い合わせ内容」カラム: 2行までのプレビュー
  const renderMessagePreview = (value: any): React.ReactNode => {
    if (value === null || value === undefined || value === "") return "-";
    const text = String(value).replace(/\s+/g, " ").trim();
    return (
      <div className="contact-message-preview" title={String(value)}>
        {text}
      </div>
    );
  };

  // 詳細の「お問い合わせ内容」: 改行をそのまま表示
  const renderMessageDetail = (value: any): React.ReactNode => {
    if (value === null || value === undefined || value === "") return "-";
    return <div className="contact-message-detail">{String(value)}</div>;
  };

  const renderReadStateDetail = (value: any): React.ReactNode => (
    <span
      className={`contact-read-badge ${value ? "is-read" : "is-unread"}`}
    >
      {value ? "既読" : "● 未読"}
    </span>
  );

  const handleSave = async (data: any) => {
    try {
      // 更新処理のみ（作成は不可）
      if (location.pathname.includes("/edit")) {
        // Connect-ES API更新リクエストを送信
        await callSystemEntityAPI('update', data);

        // ローカル状態を更新
        setContacts(prev => prev.map(item => item.id === data.id ? data : item));
        alert("更新が完了しました！");

        // 詳細ページに戻る
        navigate(`/admin/${id}`);
      }
    } catch (error) {
      console.error('❌ 保存エラー:', error);
      alert(`保存に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    }
  };

  const unreadCount = useMemo(
    () => contacts.filter((item) => !item?.isRead).length,
    [contacts]
  );

  // 新しく届いたものを先頭にする（受信日時の降順）
  const visibleContacts = useMemo(() => {
    const list = unreadOnly ? contacts.filter((item) => !item?.isRead) : [...contacts];
    const received = (item: any) => toDate(item?.createDate)?.getTime() ?? 0;
    return list.sort((a, b) => received(b) - received(a));
  }, [contacts, unreadOnly]);

  const searchUI = (
    <div className="contact-unread-bar">
      <span className="contact-unread-count">
        未読 <strong>{unreadCount}</strong> 件 / 全 {contacts.length} 件
      </span>
      <label className="contact-unread-filter">
        <input
          type="checkbox"
          checked={unreadOnly}
          onChange={(e) => setUnreadOnly(e.target.checked)}
        />
        未読のみ表示
      </label>
    </div>
  );

  return (
    <CRUDPage
      entityName="contact"
      entityDisplayName="お問い合わせ"
      basePath="/admin"
      mode={getMode()}
      data={visibleContacts}
      item={currentContact}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onSave={handleSave}
      onBack={handleBack}
      searchUI={getMode() === "list" ? searchUI : undefined}
      defaultFieldsWhenNoMetadata={CONTACT_FIELDS}
      defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
      customColumnRenderers={{
        isRead: renderReadStateCell,
        createDate: formatContactDateTime,
        readAt: formatContactDateTime,
        name: (value) => (value ? String(value) : "-"),
        email: (value) => (value ? String(value) : "-"),
        phone: (value) => (value ? String(value) : "-"),
        subject: (value) => (value ? String(value) : "-"),
        message: renderMessagePreview,
      }}
      customDetailRenderers={{
        isRead: renderReadStateDetail,
        createDate: formatContactDateTime,
        updateDate: formatContactDateTime,
        readAt: formatContactDateTime,
        message: renderMessageDetail,
      }}
    />
  );
};
